import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Check } from "@phosphor-icons/react";
import api from "@/lib/api";
import { FunnelPicker } from "@/components/funnels/FunnelPicker";
import { BuyerPriceFields } from "@/components/funnels/BuyerPriceFields";
import { FUNNEL_OPTIONS, picksLabel, picksOrAll } from "@/lib/funnelOptions";
import { EMPTY_NOTIFY, NotifyBuyerBox, notifyParams } from "@/components/NotifyBuyerBox";

const BTN = "border-2 border-black px-3 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-40";
const MODES = [
  ["live", "Buyer can change it in their portal \u2014 live right away"],
  ["approval", "Buyer can ask in their portal \u2014 an admin approves first"],
  ["admin_only", "Admins only"],
];
const DASH_MODES = [
  ["all", "All their pages"],
  ["some", "Only the pages I tick"],
];
const LINK_MODES = [
  ["forward", "Send the visitor to the buyer's first picked funnel"],
  ["keep", "Keep working — it's the buyer's own traffic"],
];

function toForm(d) {
  return {
    picks: picksOrAll(d.picks),
    pick_mode: d.pick_mode,
    link_mode: d.link_mode,
    price: d.price.base == null ? "" : String(d.price.base),
    funnel_prices: Object.fromEntries(Object.entries(d.funnel_prices || {}).map(([k, v]) => [k, String(v)])),
    dashboard_mode: d.dashboard_pages ? "some" : "all",
    dashboard_pages: d.dashboard_pages || [],
  };
}

const Radios = ({ name, options, value, onChange }) => (
  <div className="flex flex-col gap-1">
    {options.map(([v, label]) => (
      <label key={v} className="flex items-center gap-2 text-xs cursor-pointer">
        <input type="radio" name={name} className="accent-black" checked={value === v} onChange={() => onChange(v)}
               data-testid={`buyer-funnels-${name}-${v}`} />
        {label}
      </label>
    ))}
  </div>
);

const DashboardPages = ({ data, form, set }) => {
  const items = [
    ...FUNNEL_OPTIONS.map((f) => ({ slug: f.slug, label: `Shared page: ${f.title}` })),
    ...(data.own_pages || []).map((p) => ({ slug: p.slug, label: `Their page: /lp/${p.slug}${p.status !== "published" ? " (draft)" : ""}` })),
  ];
  const on = form.dashboard_pages;
  const toggle = (s) => set("dashboard_pages", on.includes(s) ? on.filter((x) => x !== s) : [...on, s]);
  return (
    <div data-testid="buyer-funnels-dashboard">
      <div className="label-mono mt-4 mb-1.5">Pages the buyer sees in their dashboard</div>
      <Radios name="dash" options={DASH_MODES} value={form.dashboard_mode} onChange={(v) => {
        set("dashboard_mode", v);
        if (v === "some" && !on.length) set("dashboard_pages", items.map((i) => i.slug));
      }} />
      {form.dashboard_mode === "some" && (
        <div className="mt-2 flex flex-col gap-1 border-2 border-black p-2" data-testid="buyer-funnels-dash-list">
          {items.map((i) => (
            <label key={i.slug} className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" className="accent-black" checked={on.includes(i.slug)} onChange={() => toggle(i.slug)}
                     data-testid={`buyer-funnels-dash-page-${i.slug}`} />
              {i.label}
            </label>
          ))}
          <div className="text-[10.5px] text-zinc-600 mt-1">
            Hidden shared pages still send leads if they&apos;re ticked above. The buyer just can&apos;t see, share or change them.
          </div>
          {!on.length && <div className="text-[10.5px] text-red-700" data-testid="buyer-funnels-dash-none">Tick at least one page, or pick &quot;All their pages&quot;.</div>}
        </div>
      )}
    </div>
  );
};

const PendingRequest = ({ pending, onReview, busy }) => (
  <div className="border-2 border-amber-600 bg-amber-50 p-2 mb-3 text-xs" data-testid="buyer-funnels-pending">
    <div className="font-bold">The buyer asked to change their funnels</div>
    <div className="mt-0.5">Requested: {picksLabel(pending.picks)}</div>
    <div className="flex gap-2 mt-2">
      <button type="button" disabled={busy} onClick={() => onReview("approve")} className={`${BTN} bg-emerald-700 text-white border-emerald-800`} data-testid="buyer-funnels-approve">
        <Check size={10} weight="bold" /> Approve
      </button>
      <button type="button" disabled={busy} onClick={() => onReview("reject")} className={`${BTN} bg-white`} data-testid="buyer-funnels-reject">
        <X size={10} weight="bold" /> Reject
      </button>
    </div>
  </div>
);

function savePayload(form, data) {
  const priceChanged = form.price !== "" && Number(form.price) !== data.price.base;
  return {
    picks: form.picks,
    pick_mode: form.pick_mode,
    link_mode: form.link_mode,
    price_per_lead: priceChanged ? Number(form.price) : null,
    funnel_prices: Object.fromEntries(
      Object.entries(form.funnel_prices).map(([k, v]) => [k, v === "" ? null : Number(v)]),
    ),
    dashboard_pages: form.dashboard_mode === "all" ? null : form.dashboard_pages,
  };
}

export const BuyerFunnelsDialog = ({ pageId, onClose, onSaved }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notify, setNotify] = useState(EMPTY_NOTIFY);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.get(`/admin/buyer-funnels/${pageId}`).then((r) => { setData(r.data); setForm(toForm(r.data)); });
  useEffect(() => {
    load().catch((e) => { toast.error(e.response?.data?.detail || "Couldn't load this buyer"); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      onSaved?.();
      return true;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't save \u2014 check the fields");
      return false;
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    if (await run(() => api.put(`/admin/buyer-funnels/${pageId}`, savePayload(form, data), notifyParams(notify)), "Pages & price saved")) onClose();
  };
  const review = async (action) => {
    const note = action === "reject" ? window.prompt("Reason for rejecting (the buyer will see this):", "") : "";
    if (note === null) return;
    if (await run(() => api.post(`/admin/buyer-profile-changes/${data.pending.change_id}/${action}`, { note: note || null }),
      action === "approve" ? "Funnel change approved \u2014 live now" : "Funnel change rejected")) await load();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" data-testid="buyer-funnels-modal">
      <div className="my-8 w-full max-w-3xl border-2 border-black bg-white shadow-[6px_6px_0_0_#000]">
        <div className="flex items-center gap-3 border-b-2 border-black px-4 py-3">
          <div>
            <div className="label-mono text-zinc-500">Pages &amp; price</div>
            <div className="font-chivo text-lg font-black leading-tight" data-testid="buyer-funnels-title">{data?.page.firm_name || "Loading\u2026"}</div>
          </div>
          <button type="button" className="ml-auto p-1 hover:bg-zinc-100" onClick={onClose} aria-label="Close" data-testid="buyer-funnels-close">
            <X size={18} weight="bold" />
          </button>
        </div>
        {!form ? <div className="label-mono p-6">Loading\u2026</div> : (
          <div className="grid gap-6 p-4 md:grid-cols-2">
            <div>
              {data.pending && <PendingRequest pending={data.pending} onReview={review} busy={busy} />}
              <div className="label-mono mb-1.5">Shared pages that send this buyer leads</div>
              <FunnelPicker value={form.picks} onChange={(v) => set("picks", v)} testId="buyer-funnels-picker" />
              <div className="label-mono mt-4 mb-1.5">Who can change this</div>
              <Radios name="mode" options={MODES} value={form.pick_mode} onChange={(v) => set("pick_mode", v)} />
              <div className="label-mono mt-4 mb-1.5">Buyer links (?b=) to funnels they didn&apos;t pick</div>
              <Radios name="link" options={LINK_MODES} value={form.link_mode} onChange={(v) => set("link_mode", v)} />
              <DashboardPages data={data} form={form} set={set} />
            </div>
            <BuyerPriceFields data={data} form={form} set={set} />
          </div>
        )}
        <div className="flex flex-wrap items-start justify-end gap-2 border-t-2 border-black px-4 py-3">
          {form && <div className="mr-auto w-full sm:w-80"><NotifyBuyerBox value={notify} onChange={setNotify} testId="buyer-funnels-notify"
            preview={() => ({ kind: "pages_and_price", page_id: pageId, picks: form.picks,
              dashboard_pages: form.dashboard_mode === "all" ? null : form.dashboard_pages })} /></div>}
          <button type="button" className={`${BTN} bg-white hover:bg-zinc-100`} onClick={onClose} data-testid="buyer-funnels-cancel">Cancel</button>
          <button type="button" className={`${BTN} bg-black text-white hover:bg-emerald-700`} onClick={save}
                  disabled={!form || busy || !form.picks.length || (form.dashboard_mode === "some" && !form.dashboard_pages.length)} data-testid="buyer-funnels-save">
            {busy ? "Saving\u2026" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};
